'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { createClient } from '@/lib/supabase/browser';
import { useAuthContext } from '@/components/providers/AuthProvider';
import { THEME_TOGGLE } from '@/lib/flags';

export type Theme = 'booth' | 'daylight';

const STORAGE_KEY = 'iv-theme';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
  /** Whether the toggle UI should render at all (mirrors the THEME_TOGGLE flag). */
  enabled: boolean;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'booth',
  setTheme: () => {},
  enabled: false,
});

function applyTheme(t: Theme) {
  document.documentElement.dataset.theme = t;
}

function isTheme(v: unknown): v is Theme {
  return v === 'booth' || v === 'daylight';
}

/**
 * Owns the live `data-theme` attribute after hydration. The pre-hydration
 * inline script in the root layout sets it first (reading localStorage,
 * defaulting to booth) to avoid a flash; this provider then keeps it in sync
 * with user actions and the persisted profile preference.
 *
 * Flag OFF: the site is hard-forced to Booth — stored prefs are ignored and
 * setTheme is a no-op — so an un-themed Daylight can never leak to users.
 */
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuthContext();
  const [theme, setThemeState] = useState<Theme>('booth');

  // Sync React state to whatever the inline script already put on <html>.
  useEffect(() => {
    if (!THEME_TOGGLE) {
      applyTheme('booth');
      setThemeState('booth');
      return;
    }
    const dom = document.documentElement.dataset.theme;
    setThemeState(isTheme(dom) ? dom : 'booth');
  }, []);

  // Reconcile with the persisted profile preference once auth resolves.
  //
  // The device's explicit choice (localStorage) is authoritative: it is written
  // synchronously the instant the user toggles, whereas the DB write is
  // best-effort and can be lost to an immediate refresh. So when a valid local
  // choice exists we keep it and — if the DB disagrees — push the local value
  // UP to heal a previously-lost write (e.g. the row still sitting at its
  // NOT NULL DEFAULT 'booth'). Only when there is NO valid local choice (a fresh
  // device / first load) do we adopt the DB value downward, preserving
  // "profile wins on a new device".
  useEffect(() => {
    if (!THEME_TOGGLE || !user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const supabase = createClient();
        const { data } = await supabase
          .from('profiles')
          .select('theme_preference')
          .eq('id', user.id)
          .maybeSingle();
        if (cancelled) return;
        const pref = data?.theme_preference;

        let local: string | null = null;
        try {
          local = localStorage.getItem(STORAGE_KEY);
        } catch {
          // storage unreadable — fall through to DB-driven behavior below.
        }

        if (isTheme(local)) {
          // Device choice wins; never clobber it with the DB value. Self-heal
          // the row if it drifted (a lost fire-and-forget write left the
          // default in place). Best-effort — the visible theme is unaffected.
          if (pref !== local) {
            const { error: healErr } = await supabase
              .from('profiles')
              .update({ theme_preference: local })
              .eq('id', user.id);
            if (healErr) console.warn('[theme] DB self-heal failed', healErr.message);
          }
          return;
        }

        // No valid local choice — adopt the persisted profile preference
        // downward and seed localStorage so this device remembers it.
        if (isTheme(pref)) {
          applyTheme(pref);
          setThemeState(pref);
          try {
            localStorage.setItem(STORAGE_KEY, pref);
          } catch {
            // private mode / storage disabled — DOM state still applies.
          }
        }
      } catch {
        // Non-critical — the locally-applied theme remains in effect.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const setTheme = useCallback(
    (t: Theme) => {
      if (!THEME_TOGGLE) return;
      applyTheme(t);
      setThemeState(t);
      try {
        localStorage.setItem(STORAGE_KEY, t);
      } catch {
        // ignore storage failures
      }
      if (user?.id) {
        const supabase = createClient();
        // Best-effort persist; the visual change already applied above, so the
        // toggle stays instant and the UI never blocks on this. Attaching
        // .then() actually dispatches the request (the PostgREST builder is
        // lazy) and surfaces failures instead of swallowing them. A write still
        // lost to an immediate refresh self-heals on next load via the
        // reconciliation effect, which re-pushes the authoritative local choice.
        void supabase
          .from('profiles')
          .update({ theme_preference: t })
          .eq('id', user.id)
          .then((res: { error: { message: string } | null }) => {
            if (res.error) console.warn('[theme] persist failed', res.error.message);
          });
      }
    },
    [user?.id]
  );

  return (
    <ThemeContext.Provider value={{ theme, setTheme, enabled: THEME_TOGGLE }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
